import type { BookingDetails } from "@/app/booking-calendar/bookingCalendarApi"
import { longDateLabel, minutesToLabel, SITE_TIME_ZONE, zonedDateAndMinutes } from "@/app/booking-calendar/lib/siteTime"
import type { ResolutionOption } from "../availability/lib/overrideEditModel"
import type { ScheduleOverrideResolveOption } from "../schedule-overrides/scheduleOverridesApi"

// Automatic reassignment of an override's affected bookings (AB#810): the pure parts the resolution
// choice, the override detail and the plan/progress dialog share.

/** What the choice says while the server's options are still being read. */
export const CHECKING_REASON = "Checking whether it can be used for this override…"

/**
 * What the server says about the server-decided options: an override's `resolve/options`, or before
 * Save the settings-only `resolve/capabilities` — both carry these two.
 */
export interface ResolveServerState {
  automatic: ScheduleOverrideResolveOption
  manual: ScheduleOverrideResolveOption
}

/**
 * The options with the server's word on automatic reassignment and manual reschedule (AB#810, AB#811):
 * each enabled only when the server says so, otherwise disabled with its reason. While the server's
 * answer loads, or when it could not be read, each says why. With nothing asked, the list stands — and
 * it lists both disabled. "Proceed" is never the server's.
 */
export function withServerState(
  options: readonly ResolutionOption[],
  server: ResolveServerState | undefined,
  state: { loading?: boolean; error?: unknown; checkingReason?: string } = {},
): ResolutionOption[] {
  return options.map(option => {
    if (option.id !== "automatic" && option.id !== "manual") return option
    if (server) {
      const said = option.id === "automatic" ? server.automatic : server.manual
      return said.enabled
        ? { ...option, enabled: true, reason: undefined }
        : { ...option, enabled: false, reason: said.reason ?? option.reason ?? "Not available for this override." }
    }
    if (state.error !== undefined && state.error !== null) {
      return { ...option, enabled: false, reason: describeResolveError(state.error, option.id === "manual" ? "Manual reschedule" : undefined) }
    }
    if (state.loading) return { ...option, enabled: false, reason: state.checkingReason ?? CHECKING_REASON }
    return option
  })
}

/**
 * What a refused options/preview/run request says. A 400 or 409 carries the server's sentence (the Cal
 * update the window bypass needs, a batch already under way, a withdrawn override, nothing affected) —
 * as text, or a problem's detail; the rest are generic.
 */
export function describeResolveError(error: unknown, what: string = "Automatic reassignment"): string {
  const { status, data } = (error ?? {}) as { status?: number | string; data?: unknown }
  const message = serverMessage(data)

  if ((status === 400 || status === 409) && message) return message
  if (status === 404) return message ?? `${what} is not available for this override.`
  if (status === 403) return "You do not have permission to reschedule these appointments."
  if (status === 409) return "Another reassignment for this override is under way. Try again once it finishes."
  return "Could not reach the server. Try again in a moment."
}

function serverMessage(data: unknown): string | null {
  if (typeof data === "string") return data.trim() !== "" ? data : null
  if (data && typeof data === "object") {
    const { detail, title } = data as { detail?: unknown; title?: unknown }
    if (typeof detail === "string" && detail.trim() !== "") return detail
    if (typeof title === "string" && title.trim() !== "") return title
  }
  return null
}

/** "Monday, Nov 30, 1:00 PM", on the sites' clock. */
export function formatInstant(instant: string, timeZone: string = SITE_TIME_ZONE): string {
  const { date, minutes } = zonedDateAndMinutes(instant, timeZone)
  return `${longDateLabel(date)}, ${minutesToLabel(minutes)}`
}

/**
 * The customer's name per Cal booking uid, from the bookings the page already lists — the plan and the
 * batch name bookings by uid only.
 */
export function customerNames(bookings: readonly BookingDetails[] | null | undefined): Map<string, string> {
  const names = new Map<string, string>()
  for (const booking of bookings ?? []) {
    const name = booking.attendees[0]?.name?.trim()
    if (name) names.set(booking.uid, name)
  }
  return names
}

/** "1 appointment" / "3 appointments". */
export function appointments(count: number): string {
  return count === 1 ? "1 appointment" : `${count} appointments`
}
